



































import {
  ref,
  computed,
  defineComponent,
  onMounted,
  onUnmounted,
  onBeforeMount,
  watch,
} from '@vue/composition-api';
import useBanners from 'frontend/composables/useBanners';
import { Banner } from 'frontend/types/banners';
import { trackClickWithElementId } from 'frontend/utils/nux';
import { useStore } from '@vueblocks/vue-use-vuex';
import { track } from 'frontend/utils/analytics';
import { postData } from 'frontend/utils/ajax';
import dayjs from 'dayjs';
import RelativeTime from 'dayjs/plugin/relativeTime';
import Duration from 'dayjs/plugin/duration';
dayjs.extend(RelativeTime);
dayjs.extend(Duration);
import { safeWindow } from 'frontend/utils/window';
import DynamicIcon from './icons/DynamicIcon.vue';

export default defineComponent({
  props: {
    legacyUrl: String,
    isAuthenticated: Boolean,
  },

  setup(props, { root }) {
    const legacyBanner = ref();
    const bannerDismissed = ref(false);
    const secondsElapsed = ref(0);
    const countdownInterval = ref();
    const store = useStore();
    const bannerEl = ref<HTMLElement>();

    onBeforeMount(async () => {
      // fetch here needed only for legacy page
      // For new pages, banner is fetched in the vuex
      if (props.legacyUrl) {
        legacyBanner.value = await useBanners(props.legacyUrl);
      }
    });

    onMounted(async () => {
      safeWindow()?.addEventListener('resize', onResize);
    });

    function onAfterEnterTransition() {
      // recalculate height after banner is shown
      onResize();
      if (banner.value?.is_sticky) {
        document.body.classList.add('has--sticky-banner');
      }
      track(
        'ui',
        {
          action: 'banner',
          element_type: 'banner',
          element_id: `viewed_${axId.value}`,
        },
        { exclude: ['Amplitude'] },
      );

      if (showCountdown.value) {
        clearInterval(countdownInterval.value);
        countdownInterval.value = setInterval(() => {
          secondsElapsed.value += 1;
        }, 1000);
      }
    }

    onUnmounted(() => {
      safeWindow()?.removeEventListener('resize', onResize);
    });

    function onAfterLeaveTransition() {
      clearInterval(countdownInterval.value);
      if (document.body.classList.contains('has--sticky-banner') && !banner.value?.is_sticky) {
        document.body.classList.remove('has--sticky-banner');
      }
      // recalculate height after banner is hidden
      onResize();
    }

    const banner = computed(() => {
      if (props.legacyUrl) {
        return legacyBanner.value;
      }
      return store.getters['banner/currentBanner'];
    });

    watch(banner, (currentBanner?: Banner, oldBanner?: Banner) => {
      if (!currentBanner || !oldBanner) {
        return;
      }
      if (JSON.stringify(currentBanner) === JSON.stringify(oldBanner)) {
        // banner actually hasn't changed, false trigger due to route change?
        return;
      }
      // When both old and new banner data are available, the banner hide/show doesn't occur,
      // so following transition events need to be triggered manually
      onAfterLeaveTransition();
      setTimeout(() => {
        onAfterEnterTransition();
      }, 10);
    });

    const iconComponent = computed(() => {
      return banner.value?.icon_component;
    });

    const destinationRouteName = computed(() => {
      return banner.value?.destination_route_name;
    });

    const axId = computed(() => {
      return banner.value?.tracking_element_id;
    });

    const loggedIn = computed(() => {
      return props.isAuthenticated ? props.isAuthenticated : store?.getters?.isAuthenticated;
    });

    async function handleClick(): Promise<void> {
      trackClickWithElementId(`clicked_${axId.value}`);
      if (props.legacyUrl) {
        await performLegacyRedirect();
      } else {
        if (banner.value?.client_action_name) {
          store.dispatch(banner.value.client_action_name);
        } else if (banner.value?.open_in_new_tab) {
          const routeData = root.$router.resolve({ name: destinationRouteName.value });
          safeWindow()?.open(routeData.href, '_blank');
        } else {
          root.$router.push({ name: destinationRouteName.value });
        }
      }
    }

    async function performLegacyRedirect(): Promise<void> {
      const win = safeWindow();
      if (!win) {
        // Nothing we can do without a window.
        alert('Sorry, but this feature is not available in your browser.');
        return;
      }
      // legacy request, let's call the api endpoint so it figures out
      // what to do since we can't touch the store or the router
      const url = '/api/v4/banners/legacy_action';
      let queryParams = {};
      if (destinationRouteName.value) {
        queryParams = { ...queryParams, destination_route_name: destinationRouteName.value };
      } else if (banner.value?.client_action_name) {
        queryParams = { ...queryParams, client_action_name: banner.value?.client_action_name };
      }
      // User should be redirected now
      try {
        const redirectData = await postData(url, queryParams);
        win!.location.href = redirectData.redirect_url;
      } catch (e) {
        alert("Sorry, we couldn't process this request at this time. Please try again later.");
      }
    }

    async function dismiss() {
      trackClickWithElementId(`dismissed_${axId.value}`);
      bannerDismissed.value = true;
      const dismissalEndpoint = banner.value?.dismissal_endpoint;
      if (dismissalEndpoint) {
        try {
          await postData(dismissalEndpoint);
        } catch (e) {
          // Ignore
        }
      }
    }

    function onResize() {
      if (store) {
        const height = bannerEl.value?.offsetHeight || 0;
        store.commit('banner/bannerHeight', height);
      }
    }

    const bannerText = computed(() => {
      return `${banner.value?.text}${countdownPhrase.value}`;
    });

    // TODO: move countdown to own composable
    const showCountdown = computed(() => {
      return !!banner.value?.countdown_remaining_seconds;
    });

    const secondsRemaining = computed(() => {
      if (showCountdown.value) {
        const remainingSeconds = banner.value?.countdown_remaining_seconds;
        return remainingSeconds - secondsElapsed.value;
      }
      return 0;
    });

    const countdownPhrase = computed(() => {
      if (showCountdown && secondsRemaining.value) {
        const endsIn = dayjs.duration(secondsRemaining.value, 'second');
        const days = endsIn.get('days');
        const hours = endsIn.get('hours');
        const minutes = endsIn.get('minutes');
        const seconds = endsIn.get('seconds');
        const d = days > 0 ? `${days}d ` : '';
        const h = hours > 0 ? `${hours.toString().padStart(2, '0')}h ` : '';
        const m = minutes > 0 ? `${minutes.toString().padStart(2, '0')}m ` : '';
        const s = `${seconds.toString().padStart(2, '0')}s`;
        return `<br class='b-small'/> <span class='countdown'>Offer ends in ${d}${h}${m}${s}.</span>`;
      }
      return '';
    });

    return {
      banner,
      bannerDismissed,
      bannerText,
      dismiss,
      handleClick,
      showCountdown,
      loggedIn,
      iconComponent,
      bannerEl,
      onAfterEnterTransition,
      onAfterLeaveTransition,
    };
  },

  components: { DynamicIcon },
});
